import * as React from "react";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "../lib/utils";
import { Card } from "./card";

export interface MetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  unit?: string;
  /** Delta text or number, e.g. "+12.4%" or 12.4 (rendered with sign). */
  delta?: string | number;
  /** Text describing the delta window, e.g. "vs last 24h". */
  deltaLabel?: string;
  /** Whether an increase is good (color direction of the delta). Default up. */
  goodWhen?: "up" | "down";
  /** Tiny inline SVG sparkline series (no chart lib). */
  sparkline?: number[];
  sparklineLabel?: string;
  /** Optional footer slot (links, timestamps). */
  footer?: React.ReactNode;
}

/** Normalize a series into an SVG polyline path. */
function sparklinePath(values: number[], width: number, height: number): string {
  if (values.length === 0) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : width;
  return values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / span) * (height - 2) - 1;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(
  (
    {
      className,
      label,
      value,
      unit,
      delta,
      deltaLabel,
      goodWhen = "up",
      sparkline,
      sparklineLabel,
      footer,
      ...props
    },
    ref,
  ) => {
    const numericDelta = typeof delta === "number" ? delta : undefined;
    const positive = numericDelta != null ? numericDelta > 0 : typeof delta === "string" && delta.trim().startsWith("+");
    const flat = numericDelta === 0 || delta === undefined || delta === null;
    const good = flat ? null : (positive && goodWhen === "up") || (!positive && goodWhen === "down");

    const TrendIcon = flat ? Minus : positive ? TrendingUp : TrendingDown;
    const trendClass =
      flat || good === null
        ? "text-muted-foreground"
        : good
          ? "text-success"
          : "text-destructive";

    const deltaText =
      delta === undefined || delta === null
        ? undefined
        : typeof delta === "number"
          ? `${delta > 0 ? "+" : ""}${delta}`
          : delta;

    return (
      <Card ref={ref} className={cn("p-5", className)} {...props}>
        <div className="flex items-start justify-between gap-4">
          <div className="text-sm font-medium text-muted-foreground">{label}</div>
          {sparkline && sparkline.length > 1 ? (
            <svg
              viewBox="0 0 96 32"
              width="96"
              height="32"
              role="img"
              aria-label={sparklineLabel ?? `Sparkline for ${typeof label === "string" ? label : "metric"}`}
              className="shrink-0 text-primary"
              focusable="false"
            >
              <path
                d={sparklinePath(sparkline, 96, 32)}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </div>
        <div className="mt-1.5 flex items-baseline gap-1">
          <span className="font-display text-2xl font-semibold tracking-tight text-foreground font-tnum">
            {value}
          </span>
          {unit ? <span className="text-sm text-muted-foreground">{unit}</span> : null}
        </div>
        {deltaText !== undefined ? (
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            <TrendIcon className={cn("size-3.5", trendClass)} aria-hidden="true" />
            <span className={cn("font-medium font-tnum", trendClass)}>{deltaText}</span>
            {deltaLabel ? <span className="text-muted-foreground">{deltaLabel}</span> : null}
          </div>
        ) : null}
        {footer ? <div className="mt-3 border-t border-border pt-3 text-xs">{footer}</div> : null}
      </Card>
    );
  },
);
MetricCard.displayName = "MetricCard";

export { MetricCard };
